import { addDays, addHours, addMinutes } from 'date-fns';
import SUPPORTING_DOCS from '../../supportingDocs';
import { RuleProps, FieldValue } from 'types/application';
import { formatDateTime } from 'features/applicationPage/helpers';

export const SECT_PE = 'pe-details';
export const SECT_EVENT_DETAILS = 'event-details';
export const SECT_EVENT_VENUE = 'event-venue';
export const SECT_PERFORMER = 'performer';
export const SECT_DOCS = 'supporting-docs';

export const AEL_EVENT_FIELDS = [
    {
        sectionID: SECT_EVENT_DETAILS,
        sectionName: 'Event Details',
        fieldList: [
            {
                id: 'event-title',
                apiID: 'Title',
                label: 'Event Title',
                type: 'text'
            },
            {
                id: 'language',
                apiID: 'Language',
                label: 'Language',
                type: 'dropdown'
            },
            {
                id: 'event-type',
                apiID: 'IMDA-IRIS-Work-Arts-Type',
                label: 'Event Type',
                type: 'dropdown'
            },
            {
                id: 'event-sub-type',
                apiID: 'SubType',
                label: 'Event Sub Type',
                type: 'text'
            },
            {
                id: 'in-out',
                label: 'Type of Venue',
                apiID: 'IndoorOutdoor',
                type: 'text'
            },
            {
                id: 'event-desc',
                label: 'Event Description',
                apiID: 'Description',
                type: 'longText',
                span: 12,
                textRows: 4,
                hasCharCounter: true,
                rule: { required: true, charLength: 5000 }
            },
            {
                id: 'form-of-public-entertainment',
                label: 'Any form of public entertainment?',
                type: 'text'
            },
            {
                id: 'offered-online',
                label: 'Offered online',
                apiID: '',
                type: 'text'
            },
            {
                id: 'event-date-time',
                label: 'Event Date & Time',
                type: 'table',
                rule: { required: true },
                labelNote: 'Event Start Date must not be less than 7 days from application date.',
                subFields: [
                    {
                        id: 'from',
                        header: 'From',
                        type: 'dateTime',
                        rule: {
                            baseIds: [`${SECT_PE}-pe-start`, `${SECT_PE}-pe-end`, `${SECT_EVENT_DETAILS}-event-date-time.idx.to`],
                            dateLimit: ({ baseValues }: RuleProps) => {
                                const baseStartDate =
                                    baseValues && baseValues[`${SECT_PE}-pe-start`]
                                        ? new Date(baseValues[`${SECT_PE}-pe-start`] as string)
                                        : new Date();
                                const baseEndDate =
                                    baseValues && baseValues[`${SECT_PE}-pe-end`] && new Date(baseValues[`${SECT_PE}-pe-end`] as string);
                                return {
                                    minDate: baseStartDate <= addDays(new Date(), 7) ? addDays(new Date(), 7) : baseStartDate,
                                    maxDate: baseEndDate
                                };
                            },
                            required: true,
                            revalidate: [`${SECT_EVENT_DETAILS}-event-date-time.idx.to`],
                            customValidate: ({ currentValue, baseValues }: RuleProps) => {
                                const peStart = baseValues && (baseValues[`${SECT_PE}-pe-start`] as string);
                                const peEnd = baseValues && (baseValues[`${SECT_PE}-pe-end`] as string);
                                if (currentValue) {
                                    const current = new Date(currentValue as string);
                                    const start = Boolean(peStart && new Date(peStart) > current);
                                    const end = Boolean(peEnd && addMinutes(addHours(new Date(peEnd), 23), 59) < current);
                                    if (start || end) {
                                        return 'Event Date/ Time From should fall between the PE Licence Period Start and End Date';
                                    }

                                    const ltDay7 = addDays(new Date(), 7) > current;
                                    const today = formatDateTime(new Date().toISOString(), 'date');
                                    if (ltDay7) return `Event Start Date/Time must be at least 7 calendar days from ${today}`;
                                }
                                const idTo =
                                    baseValues &&
                                    Object.keys(baseValues).find((key: string) => {
                                        const keySplit = key.split('.');
                                        if (keySplit.length === 3) {
                                            const baseID = `${SECT_EVENT_DETAILS}-event-date-time.idx.to`;
                                            return baseID.includes(keySplit[0]) && baseID.includes(keySplit[2]);
                                        }
                                        return false;
                                    });
                                const baseTo = baseValues && idTo && baseValues[idTo] && new Date(baseValues[idTo] as string);
                                if (baseTo && currentValue && baseTo < new Date(currentValue as string)) {
                                    return 'Date/Time From cannot be later than Date/Time To';
                                }
                                if (baseTo && currentValue && new Date(baseTo).getTime() === new Date(currentValue as string).getTime()) {
                                    return 'Date/Time From cannot be the same as Date/Time To';
                                }
                                return true;
                            }
                        }
                    },
                    {
                        id: 'to',
                        header: 'To',
                        type: 'dateTime',
                        rule: {
                            baseIds: [`${SECT_EVENT_DETAILS}-event-date-time.idx.from`, `${SECT_PE}-pe-end`],
                            dateLimit: ({ baseValues }: RuleProps) => {
                                const idFrom =
                                    baseValues &&
                                    Object.keys(baseValues).find((key: string) => {
                                        const keySplit = key.split('.');
                                        if (keySplit.length === 3) {
                                            const baseID = `${SECT_EVENT_DETAILS}-event-date-time.idx.from`;
                                            return baseID.includes(keySplit[0]) && baseID.includes(keySplit[2]);
                                        }
                                        return false;
                                    });
                                const baseStartDate = baseValues && idFrom && baseValues[idFrom] && new Date(baseValues[idFrom] as string);
                                const baseEndDate =
                                    baseValues && baseValues[`${SECT_PE}-pe-end`] && new Date(baseValues[`${SECT_PE}-pe-end`] as string);
                                return {
                                    minDate: baseStartDate,
                                    maxDate: baseEndDate
                                };
                            },
                            required: true,
                            revalidate: [`${SECT_EVENT_DETAILS}-event-date-time.idx.from`],
                            customValidate: ({ currentValue, baseValues }: RuleProps) => {
                                const peStart = baseValues && (baseValues[`${SECT_PE}-pe-start`] as string);
                                const peEnd = baseValues && (baseValues[`${SECT_PE}-pe-end`] as string);
                                if (currentValue) {
                                    const current = new Date(currentValue as string);
                                    const start = Boolean(peStart && new Date(peStart) > current);
                                    const end = Boolean(peEnd && addMinutes(addHours(new Date(peEnd), 23), 59) < current);
                                    if (start || end) {
                                        return 'Event Date/ Time To should fall between the PE Licence Period Start and End Date';
                                    }
                                }
                                const idFrom =
                                    baseValues &&
                                    Object.keys(baseValues).find((key: string) => {
                                        const keySplit = key.split('.');
                                        if (keySplit.length === 3) {
                                            const baseID = `${SECT_EVENT_DETAILS}-event-date-time.idx.from`;
                                            return baseID.includes(keySplit[0]) && baseID.includes(keySplit[2]);
                                        }
                                        return false;
                                    });
                                const baseFrom = baseValues && idFrom && baseValues[idFrom] && new Date(baseValues[idFrom] as string);

                                if (
                                    baseFrom &&
                                    currentValue &&
                                    new Date(baseFrom).getTime() === new Date(currentValue as string).getTime()
                                ) {
                                    return 'Date/Time To cannot be same as Date/Time From';
                                }
                                if (baseFrom && currentValue && baseFrom > new Date(currentValue as string)) {
                                    return 'Date/Time From cannot be later than Date/Time To';
                                }

                                return true;
                            }
                        }
                    }
                ],
                span: 12
            },
            {
                id: 'admission-fee',
                label: 'Admission Fee/Seat Price',
                apiID: 'AdmissionFee',
                type: 'text'
            },
            {
                id: 'maximum-seats-per-show',
                label: 'Maximum No. of Seats per Show',
                type: 'text'
            },
            {
                id: 'crowd-size',
                label: 'Expected Crowd Size',
                apiID: 'CrowdSize',
                type: 'text'
            }
        ]
    },
    {
        sectionID: SECT_EVENT_VENUE,
        sectionName: 'Event Venue',
        fieldList: [
            {
                id: 'address-type',
                label: 'Address Type',
                type: 'dropdown',
                apiID: 'AddressType',
                rule: { required: true },
                options: [
                    {
                        optID: 'SG',
                        optLabel: 'Singapore'
                    },
                    {
                        optID: 'UNF',
                        optLabel: 'Unformatted'
                    }
                ]
            },
            {
                id: 'building',
                label: 'Building Name',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_EVENT_VENUE}-address-type`],
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(!baseValues || !baseValues?.[`${SECT_EVENT_VENUE}-address-type`]);
                        return result;
                    }
                }
            },
            {
                id: 'postal',
                label: 'Postal Code',
                apiID: 'PostalCode',
                type: 'postal',
                rule: {
                    baseIds: [`${SECT_EVENT_VENUE}-address-type`],
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_VENUE}-address-type`] === 'SG');
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_VENUE}-address-type`] !== 'SG');
                        return result;
                    }
                }
            },
            {
                id: 'block-house',
                label: 'Block/House No.',
                apiID: 'BlockNumber',
                type: 'dropdown',
                rule: {
                    baseIds: [`${SECT_EVENT_VENUE}-address-type`],
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_VENUE}-address-type`] === 'SG');
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_VENUE}-address-type`] !== 'SG');
                        return result;
                    }
                }
            },
            {
                id: 'street',
                label: 'Street Name',
                apiID: 'StreetName',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_EVENT_VENUE}-address-type`],
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_VENUE}-address-type`] === 'SG');
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_VENUE}-address-type`] !== 'SG');
                        return result;
                    }
                }
            },
            {
                id: 'floor',
                label: 'Floor No.',
                apiID: 'FloorNumber',
                type: 'text',
                span: 2,
                placeholder: 'Eg: 01, B1, 01M',
                rule: {
                    baseIds: [`${SECT_EVENT_VENUE}-address-type`],
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_VENUE}-address-type`] !== 'SG');
                        return result;
                    }
                }
            },
            {
                id: 'unit',
                label: 'Unit No.',
                apiID: 'UnitNumber',
                type: 'text',
                span: 2,
                placeholder: 'Eg: #05-01, use 01',
                rule: {
                    baseIds: [`${SECT_EVENT_VENUE}-address-type`],
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_EVENT_VENUE}-address-type`] !== 'SG');
                        return result;
                    }
                }
            },
            {
                id: 'desc-venue',
                label: 'Description of Venue',
                apiID: 'LocationDescription',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_EVENT_VENUE}-address-type`],
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(!baseValues || !baseValues?.[`${SECT_EVENT_VENUE}-address-type`]);
                        return result;
                    }
                }
            }
        ]
    },
    {
        sectionID: 'performer',
        sectionName: 'Performer Details',
        note: "You may choose to provide the list of performers in the table below and/or via an attachment. For performances by groups/bands, please provide the group/band name(s) and all the members' names.",
        fieldList: [
            {
                id: 'performer-list',
                label: 'Performer',
                type: 'table',
                subFields: [
                    {
                        id: 'artist',
                        header: 'Name of Artist ',
                        type: 'text'
                        // rule: {
                        //     optionalRequired: true,
                        //     optionalRequiredMsg: '',
                        //     baseIds: [`${SECT_PERFORMER}-provide-performer-list`, `${SECT_PERFORMER}-performerListDocs`],
                        //     required: ({ baseValues }: RuleProps) => {
                        //         const result = Boolean(
                        //             baseValues &&
                        //                 baseValues[`${SECT_PERFORMER}-provide-performer-list`] === 'Yes' &&
                        //                 ((baseValues[`${SECT_PERFORMER}-performerListDocs`] as Attachment[]) || []).filter(
                        //                     (file: Attachment) => !file.error && !file.deleted
                        //                 ).length === 0
                        //         );
                        //         return result;
                        //     }
                        // }
                    },
                    {
                        id: 'group-band',
                        header: 'Group/Band Name',
                        type: 'text'
                    },
                    {
                        id: 'country',
                        header: 'Country',
                        apiID: 'Country',
                        type: 'dropdown'
                        // rule: {
                        //     optionalRequired: true,
                        //     optionalRequiredMsg: '',
                        //     baseIds: [`${SECT_PERFORMER}-provide-performer-list`, `${SECT_PERFORMER}-performerListDocs`],
                        //     required: ({ baseValues }: RuleProps) => {
                        //         const result = Boolean(
                        //             baseValues &&
                        //                 baseValues[`${SECT_PERFORMER}-provide-performer-list`] === 'Yes' &&
                        //                 ((baseValues[`${SECT_PERFORMER}-performerListDocs`] as Attachment[]) || []).filter(
                        //                     (file: Attachment) => !file.error && !file.deleted
                        //                 ).length === 0
                        //         );
                        //         return result;
                        //     }
                        // }
                    }
                ],
                span: 12
                // rule: {
                //     optionalRequired: true,
                //     optionalRequiredMsg: '',
                //     revalidate: [`${SECT_PERFORMER}-performerListDocs`],
                //     baseIds: [`${SECT_PERFORMER}-provide-performer-list`, `${SECT_PERFORMER}-performerListDocs`],
                //     required: ({ baseValues }: RuleProps) => {
                //         const validFiles = baseValues
                //             ? ((baseValues[`${SECT_PERFORMER}-performerListDocs`] as Attachment[]) || []).filter(
                //                   (item: Attachment) => !item.deleted && !item.error
                //               )
                //             : [];

                //         const result = Boolean(
                //             baseValues && baseValues[`${SECT_PERFORMER}-provide-performer-list`] === 'Yes' && !validFiles.length
                //         );
                //         return result;
                //     },
                //     hidden: ({ baseValues }: RuleProps) => {
                //         const result = Boolean(baseValues && baseValues[`${SECT_PERFORMER}-provide-performer-list`] !== 'Yes');
                //         return result;
                //     }
                // }
            },
            {
                id: 'performerListDocs',
                label: 'Uploaded Performer List',
                type: 'file',
                category: 'PerformerDetails',
                span: 12,
                rule: {
                    optionalRequired: true,
                    optionalRequiredMsg: '',
                    revalidate: [`${SECT_PERFORMER}-performer-list`],
                    baseIds: [`${SECT_PERFORMER}-provide-performer-list`, `${SECT_PERFORMER}-performer-list`],
                    required: ({ baseValues }: RuleProps) => {
                        const base =
                            baseValues &&
                            ((baseValues[`${SECT_PERFORMER}-performer-list`] as { [key: string]: FieldValue }[]) || []).filter(
                                (values: { [key: string]: FieldValue }) => !values.deleted
                            );
                        const result = Boolean(
                            baseValues && baseValues[`${SECT_PERFORMER}-provide-performer-list`] === 'Yes' && !(base || []).length
                        );
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(baseValues && baseValues[`${SECT_PERFORMER}-provide-performer-list`] !== 'Yes');
                        return result;
                    }
                },
                mimeTypes: [
                    'application/pdf',
                    'application/vnd.ms-excel',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                ],
                fileTypes: ['.pdf', '.xls', '.xlsx', '.doc', '.docx']
            }
        ]
    },
    {
        sectionID: SECT_DOCS,
        sectionName: 'Supporting Documents',
        fieldList: [
            {
                id: 'docs-list',
                type: 'helperList',
                span: 12,
                preField: {
                    id: 'link',
                    label: 'Link to Supporting Documents',
                    placeholder: 'Eg: https://yourdocumentlink.com/',
                    type: 'url'
                },
                mainField: {
                    id: 'docs',
                    label: 'Upload Documents',
                    type: 'file',
                    category: 'SupportingDocuments',
                    fileTypes: ['.jpeg', '.jpg', '.png', '.bmp', '.mp4', '.mpeg', '.mov', '.pdf', '.xls', '.doc', '.xlsx', '.docx'],
                    mimeTypes: [
                        'application/pdf',
                        'application/vnd.ms-excel',
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'application/msword',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        // 'text/plain',
                        'image/jpeg',
                        'image/jpg',
                        'image/png',
                        // 'image/gif',
                        'image/bmp',
                        'video/quicktime',
                        'video/mp4',
                        'video/mpeg'
                    ]
                },
                customList: SUPPORTING_DOCS,
                customListExpect: [`${SECT_EVENT_DETAILS}-event-type`, `${SECT_EVENT_DETAILS}-event-sub-type`]
            }
        ]
    }
];
